<template>
  <div :class="$style.wsRoot" v-ws-action="wsActions">
    <span
      v-if="tournamentChannel"
      v-ws-channel="tournamentChannel"
      :class="$style.wsChannel"
      aria-hidden="true"
    />
    <span
      v-if="stageChannel"
      v-ws-channel="stageChannel"
      :class="$style.wsChannel"
      aria-hidden="true"
    />

    <app-layout-columns>
      <template #aside v-if="!isTablet">
        <div :class="$style.aside">
          <intro-card
            v-if="tournament"
            :stages="stages"
            :tournament="tournament"
            :model-value="activeStageId"
            @update:model-value="onStageSelect($event, 'sidebar')"
            @open-guide="onOpenGuide"
          />
          <player-stats-card
            v-if="isAuthorized"
            @open-leaderboard="onOpenLeaderboard"
          />
        </div>
      </template>

      <div :class="$style.main">
        <leader-board
          v-if="isLeaderboardOpen && tournament"
          :tournament="tournament"
          :stages="stages"
          :active-stage-id="activeStageId"
          @close="onCloseLeaderboard"
        />
        <template v-else>
          <details-component
            v-if="tournament"
            :tournament="tournament"
            :is-tournament-finished="isFinished"
            :stages="stages"
            :active-stage-id="activeStageId"
            @open-leaderboard="onOpenLeaderboard"
            @update:model-value="onStageSelect($event, 'stage_carousel')"
            @open-guide="onOpenGuide"
          />
          <stage-view
            v-if="activeStage"
            :stage="activeStage"
            :stage-source-block="lastStageSourceBlock"
            :compact-mode="false"
          />
          <common-zero-data v-else-if="!isFetching" :title="t('noStages')" />
          <div :class="$style.allTournamentsWrapper" v-if="isTablet">
            <all-tournaments-link />
          </div>
        </template>
      </div>
    </app-layout-columns>
  </div>
</template>

<i18n lang="yaml">
ru:
  noStages: У турнира пока нет этапов
</i18n>

<script lang="ts" setup>
import { computed, defineAsyncComponent, ref, watch } from 'vue'
import LeaderBoard from '../components/leaderboard/LeaderBoard.vue'
import { useStorage } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import AppLayoutColumns from '~/components/layout/columns/index.vue'
import CommonZeroData from '~/components/common/zero-data/index.vue'
import { useAccountStore } from '~/stores/account'
import { useTournamentStore } from '../stores/tournament'
import { useRewardsStore } from '../stores/rewards'
import { useStageStore } from '../stores/stage'
import AllTournamentsLink from '../components/tournament/AllTournamentsLink.vue'
import DetailsComponent from '../components/tournament/Details.vue'
import IntroCard from '../components/tournament/IntroCard.vue'
import PlayerStatsCard from '../components/tournament/PlayerStatsCard.vue'
import StageView from '../components/stage/StageView.vue'
import {
  usePickemAnalytics,
  type PickemStageSourceBlock
} from '../composables/use-pickem-analytics'
import { usePickemWebSocket } from '../composables/use-pickem-websocket'
import { usePickemLeaderboardRoute } from '../composables/use-pickem-leaderboard-route'

defineOptions({ name: 'PickemTournamentPage' })

// Tournament.vue — это view-компонент. Загрузка данных живёт в роутер-компонентах
// (pages/pickem/index.vue, pages/pickem/[id].vue, LastFinishedFallback.vue),
// чтобы можно было ветвить рендер по результату /tournaments/active без лишних
// запросов. Здесь мы просто читаем из useTournamentStore.

const { t } = useI18n()

const { $matchMedia } = useNuxtApp()
const isTablet = computed(() => $matchMedia.down('md'))

const accountStore = useAccountStore()
const { isAuthorized } = storeToRefs(accountStore)

const tournamentStore = useTournamentStore()
const rewardsStore = useRewardsStore()
const stageStore = useStageStore()
const { tournament, stages, isFinished } = storeToRefs(tournamentStore)
const {
  trackScreenView,
  trackLeaderboardClick,
  trackStageClick,
  trackGuideClick
} = usePickemAnalytics()

const isFetching = computed(() => false)

const activeStageId = ref<number | null>(null)
const {
  isOpen: isLeaderboardOpen,
  open: openLeaderboardRoute,
  close: closeLeaderboardRoute
} = usePickemLeaderboardRoute()
const lastStageSourceBlock = ref<PickemStageSourceBlock>('stage_carousel')
const pendingStageSource = ref<PickemStageSourceBlock | null>(null)

const { tournamentChannel, stageChannel, wsActions } = usePickemWebSocket({
  tournamentId: computed(() => tournament.value?.id),
  activeStageId,
  showLeaderboard: isLeaderboardOpen
})

const pickInitialStageId = () => {
  if (!stages.value.length) {
    activeStageId.value = null
    return
  }

  const firstActive = stages.value.find(stage => stage.status !== 'finished')
  activeStageId.value = (firstActive ?? stages.value[0]).id
}

watch(() => tournament.value?.id, pickInitialStageId, { immediate: true })

const activeStage = computed(
  () => stages.value.find(stage => stage.id === activeStageId.value) ?? null
)

const userProgressMax = computed(() => {
  let max = 0
  for (const level of rewardsStore.levels) {
    if (level.points_required > max) max = level.points_required
  }
  return max
})

const sendScreenView = () => {
  if (isLeaderboardOpen.value || !tournament.value || !activeStage.value) return

  trackScreenView({
    tournament: tournament.value,
    stage: activeStage.value,
    userProgressPoints: rewardsStore.userXp ?? tournament.value.user_xp ?? 0,
    userProgressMax: userProgressMax.value
  })
}

watch(
  [() => tournament.value?.id, activeStageId, isLeaderboardOpen],
  () => {
    if (!isLeaderboardOpen.value) {
      sendScreenView()
    }
  },
  { immediate: true }
)

const onStageSelect = (stageId: number, source: PickemStageSourceBlock) => {
  pendingStageSource.value = source
  activeStageId.value = stageId
}

watch(activeStageId, (stageId, previousStageId) => {
  if (
    stageId === null ||
    stageId === previousStageId ||
    !pendingStageSource.value ||
    !tournament.value
  ) {
    return
  }

  const stage = stages.value.find(item => item.id === stageId)
  if (!stage) return

  lastStageSourceBlock.value = pendingStageSource.value

  trackStageClick({
    stage,
    tournament: tournament.value,
    sourceBlock: pendingStageSource.value,
    unlock: stageStore.data?.id === stageId ? stageStore.data.unlock : null
  })

  pendingStageSource.value = null
})

const onOpenLeaderboard = () => {
  if (!tournament.value) return
  trackLeaderboardClick(tournament.value)
  openLeaderboardRoute()
}

const onCloseLeaderboard = () => {
  closeLeaderboardRoute()
  sendScreenView()
}

const isGuideClosed = useStorage('is-pickem-guide-closed', false)
const isFinishedGuideClosed = useStorage(
  'is-pickem-finished-guide-closed',
  false
)

const onOpenGuide = (isFirstOpen?: boolean) => {
  const { $dialog } = useNuxtApp()
  const PickemWelcomeGuideDialog = defineAsyncComponent(
    () => import('../components/tournament/PickemWelcomeGuideDialog.vue')
  )
  $dialog({
    wrapper: null,
    content: PickemWelcomeGuideDialog,
    closeOnChangeRoute: false,
    onClose: () => {
      isGuideClosed.value = true
    }
  })

  if (!isFirstOpen && tournament.value) {
    trackGuideClick({ tournament: tournament.value })
  }
}

const onOpenFinishedGuide = () => {
  const { $dialog } = useNuxtApp()
  const PickemFinishedTournamentDialog = defineAsyncComponent(
    () => import('../components/tournament/PickemFinishedTournamentDialog.vue')
  )
  $dialog({
    wrapper: null,
    content: PickemFinishedTournamentDialog,
    closeOnChangeRoute: false,
    contentProps: {
      onOpenLeaderboard: () => onOpenLeaderboard()
    },
    onClose: () => {
      isFinishedGuideClosed.value = true
    }
  })
}

onMounted(() => {
  if (isFinished.value) {
    if (!isFinishedGuideClosed.value) {
      onOpenFinishedGuide()
    }
    return
  }

  if (!isGuideClosed.value) {
    onOpenGuide(true)
  }
})
</script>

<style lang="scss" module>
.wsRoot {
  display: contents;
}

.wsChannel {
  display: none;
}

.aside {
  display: flex;
  flex-direction: column;
  gap: 1.6em;
}

.main {
  display: flex;
  flex-direction: column;
  height: 100%;

  @include down(sm) {
    gap: 1.6em;
    background: rgba(var(--root-rgb-bg-special-1), 1);
    margin: calc(0em - var(--layout-app-gap));
    padding-bottom: 0;
  }
}

.allTournamentsWrapper {
  padding: 0 1.6em 2em;
  border-radius: 0 0 1.2em 1.2em;
  background: rgba(var(--root-rgb-bg-special-1));
}
</style>
